/**
 * Structured Data Registry
 * 
 * This module provides a unified system for identifying and managing different types
 * of structured data in reports. It allows for automatic detection of data types
 * based on their structure rather than report type names.
 */
function isIRMCardiaqueData(data) {
  if (!data || typeof data !== 'object') return false;
  
  // Check for presence of IRM cardiaque specific keys
  const irmKeys = ['cinetique', 'viabilite', 'perfusion'];
  const hasIRMKeys = irmKeys.some(key => key in data);
  
  return hasIRMKeys;
}

/**
 * Check if data has stress test structure (ETT de stress)
 * Stress tests have multiple stages: repos, low_dose, pic_stress, recuperation
 */
function isStressTestData(data) {
  if (!data || typeof data !== 'object') return false;
  
  // Check for presence of stress test stages
  const stressStages = ['repos', 'low_dose', 'pic_stress', 'recuperation'];
  const hasStressStages = stressStages.some(stage => stage in data);
  
  // Also check for legacy "stress_leger" key
  const hasLegacyStress = 'stress_leger' in data;
  
  return hasStressStages || hasLegacyStress;
}

/**
 * Check if data has standard ETT/Echo structure
 * Standard tests have segment data directly without stages
 */
function isStandardEchoData(data) {
  if (!data || typeof data !== 'object') return false;

  // Check if it's NOT a stress test first
  if (isStressTestData(data)) return false;

  // Check for presence of typical segment keys
  const segmentKeys = [
    'basal_anterior', 'basal_anterolateral', 'basal_inferolateral',
    'basal_inferior', 'basal_inferoseptal', 'basal_anteroseptal',
    'mid_anterior', 'mid_anterolateral', 'mid_inferolateral',
    'mid_inferior', 'mid_inferoseptal', 'mid_anteroseptal',
    'apical_anterior', 'apical_lateral', 'apical_inferior', 'apical_septal',
    'apex'
  ];

  // Check if data contains at least one segment key
  return segmentKeys.some(key => key in data);
}

/**
 * Check if data has Hugett echo codes structure
 * Hugett reports contain structured codes in llmCodes or autoCodes arrays
 */
function isHugettEchoData(data) {
  if (!data || typeof data !== 'object') return false;

  // Check for new format: llmCodes or autoCodes arrays
  const hasLlmCodes = Array.isArray(data.llmCodes) && data.llmCodes.length > 0;
  const hasAutoCodes = Array.isArray(data.autoCodes) && data.autoCodes.length > 0;

  return hasLlmCodes || hasAutoCodes;
}

/**
 * Structured Data Type Definitions
 * Each type has:
 * - id: Unique identifier
 * - name: Display name
 * - detector: Function to detect if data matches this type
 * - component: Which component to use for visualization
 * - parser: Optional function to parse/normalize the data
 */
const STRUCTURED_DATA_TYPES = {
  STRESS_TEST: {
    id: 'stress_test',
    name: 'ETT de stress',
    detector: isStressTestData,
    component: 'StressBullseye',
    parser: (data) => {
      // Handle legacy "stress_leger" key if needed
      if (data.stress_leger && !data.low_dose) {
        return {
          ...data,
          low_dose: data.stress_leger,
          stress_leger: undefined
        };
      }
      return data;
    }
  },
  
  STANDARD_ECHO: {
    id: 'standard_echo',
    name: 'ETT Standard',
    detector: isStandardEchoData,
    component: 'StandardBullseye',
    parser: null
  },

  IRM_CARDIAQUE: {
    id: 'irm_cardiaque',
    name: 'IRM Cardiaque',
    detector: isIRMCardiaqueData,
    component: 'IRMCardiaqueBullseye',
    parser: null
  },

  HUGETT_ECHO: {
    id: 'hugett_echo',
    name: 'Hugett - Codes Échocardiographiques',
    detector: isHugettEchoData,
    component: 'EchoCodesManager',
    parser: null
  }
};

  // Future structured data types can be added here:
  // CORONARY_ANGIO: {
  //   id: 'coronary_angio',
  //   name: 'Coronarographie',
  //   detector: isCoronaryAngioData,
  //   component: 'CoronaryDiagram',
  //   parser: null
  // }
  

/**
 * Registry class for managing structured data types
 */
class StructuredDataRegistry {
  constructor() {
    this.types = { ...STRUCTURED_DATA_TYPES };
  }

  /**
   * Detect the type of structured data
   * @param {Object} data - The structured data to analyze
   * @returns {Object|null} The detected type definition or null if not recognized
   */
  detectType(data) {
    if (!data || typeof data !== 'object') return null;
    
    // Try to detect type based on data structure
    for (const typeKey in this.types) {
      const type = this.types[typeKey];
      if (type.detector && type.detector(data)) {
        return type;
      }
    }
    
    return null;
  }

  /**
   * Get a specific type by ID
   * @param {string} typeId - The type ID
   * @returns {Object|null} The type definition or null
   */
  getType(typeId) {
    for (const typeKey in this.types) {
      if (this.types[typeKey].id === typeId) {
        return this.types[typeKey];
      }
    }
    return null;
  }

  /**
   * Register a new structured data type
   * @param {string} key - Unique key for the type
   * @param {Object} typeDefinition - Type definition object
   */
  registerType(key, typeDefinition) {
    if (!typeDefinition.id || !typeDefinition.name || !typeDefinition.detector || !typeDefinition.component) {
      throw new Error('Type definition must include id, name, detector, and component');
    }
    this.types[key] = typeDefinition;
  }

  /**
   * Check if data has any recognized structured data
   * @param {Object} data - The data to check
   * @returns {boolean} True if data is recognized as structured data
   */
  hasStructuredData(data) {
    return this.detectType(data) !== null;
  }

  /**
   * Parse/normalize data based on its type
   * @param {Object} data - The data to parse
   * @returns {Object} Parsed data
   */
  parseData(data) {
    const type = this.detectType(data);
    if (type && type.parser) {
      return type.parser(data);
    }
    return data;
  }

  /**
   * Get component name for the data type
   * @param {Object} data - The structured data
   * @returns {string|null} Component name or null
   */
  getComponentForData(data) {
    const type = this.detectType(data);
    return type ? type.component : null;
  }
}

// Create and export a singleton instance
const registry = new StructuredDataRegistry();

export default registry;
export {
  STRUCTURED_DATA_TYPES,
  isStressTestData,
  isStandardEchoData,
  isIRMCardiaqueData,
  isHugettEchoData
};